import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
} from 'react';
import styled, { css } from 'styled-components';

import type { SessionContextUsage } from '@paradigm/terminal-core';

import { MENU_SURFACE } from '../../styles/menu';
import { formatContextTooltip, formatTokens } from './context-meter-format';
import { restoreComposerFocus } from '../../focus';

interface Props {
  readonly usage: SessionContextUsage | null;
}

/**
 * Fill ratios at which the meter becomes a warning. Matched to the gateway's
 * own thresholds so the terminal and openclaw's control UI agree about when a
 * session is under pressure.
 */
const WARN_RATIO = 0.85;
const DANGER_RATIO = 0.95;

/**
 * Dwell before the hover tooltip appears. Far below the ~1s the platform's own
 * tooltip waits, which is why this surface does not use `title`.
 */
const HINT_DELAY_MS = 150;

/**
 * Ring geometry, in a 16-unit viewBox rendered at 14px. The arc is a stroked
 * circle whose dash pattern is one full circumference, offset backwards by the
 * unfilled share.
 */
const RING_RADIUS = 6;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const RING_SIZE_PX = 14;

/**
 * Padding around the ring, and the slot width that follows. Shared by `Trigger`
 * and `Placeholder` so the placeholder is exactly as wide as the control it
 * stands in for; jsdom does no layout, so a test can only compare the two
 * declarations, which it does.
 */
const TRIGGER_PADDING_PX = 4;
const SLOT_SIZE_PX = RING_SIZE_PX + TRIGGER_PADDING_PX * 2;

/**
 * Shortest arc drawn for a measured chat. A floored 0% offsets the dash by the
 * whole circumference and draws nothing — pixel-identical to the bare track
 * that means "not measured yet", which a real ~9k prompt on a 1M window would
 * hit. Applied to the ARC only; the label, tooltip and legend keep the true
 * floored figure.
 */
const MIN_ARC_PERCENT = 1;

/**
 * Whole percent, rounded DOWN and clamped to 0..100.
 *
 * Floor never overstates how full the window is, and it stops the number
 * walking backwards: `usedTokens` is the last run's prompt size, not a running
 * total, so it drifts tens of tokens a turn and under rounding an 89-token
 * wobble read as the meter LOSING a percent. A genuine drop still shows.
 *
 * Colour thresholds read the exact ratio instead, so flooring can never delay
 * a pressure warning.
 */
function toPercent(ratio: number): number {
  return Math.min(100, Math.max(0, Math.floor(ratio * 100)));
}

/**
 * Context-window meter for the active chat, with the used/free breakdown
 * behind a click.
 *
 * Used vs. free is the only split the gateway can vouch for; see
 * `SessionContextUsage` for what is deliberately not shown and why.
 *
 * With no reading — a fresh chat, or a gateway too old to carry the fields —
 * it holds its slot rather than unmounting, because the provider clears the
 * reading on every chat switch and reconnect and the footer would jump each
 * time.
 */
export default function ContextMeter({ usage }: Props) {
  const [open, setOpen] = useState(false);
  const [hinted, setHinted] = useState(false);
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showHint = useCallback((delayMs: number) => {
    if (hintTimerRef.current != null) clearTimeout(hintTimerRef.current);
    // Keyboard focus skips the dwell: there is no cursor path to disambiguate,
    // and a caret that arrived here was aimed here.
    if (delayMs <= 0) {
      setHinted(true);
      return;
    }
    hintTimerRef.current = setTimeout(() => setHinted(true), delayMs);
  }, []);

  const hideHint = useCallback(() => {
    if (hintTimerRef.current != null) {
      clearTimeout(hintTimerRef.current);
      hintTimerRef.current = null;
    }
    setHinted(false);
  }, []);

  // Drop a pending timer on unmount so a dwell the user started and then
  // navigated away from can't fire into a dead component. Clears the TIMER
  // only — `hideHint` would also set state, which is pointless while
  // unmounting and something React complains about.
  useEffect(
    () => () => {
      if (hintTimerRef.current != null) clearTimeout(hintTimerRef.current);
    },
    [],
  );
  /**
   * Close, handing the caret back to the composer so the user can type
   * straight after reading the panel.
   *
   * Deferred a frame then re-checked, because a close triggered by clicking
   * elsewhere must let THAT target keep the focus it took; only a close
   * leaving focus on our own trigger is ours to redirect.
   */
  const close = useCallback(() => {
    setOpen(false);
    // `hideHint`, not `setHinted(false)`: a dwell timer armed a moment ago is
    // still pending, and would flip the tooltip back on after the close.
    hideHint();
    requestAnimationFrame(() => {
      // Only a close that leaves focus on our own trigger is ours to redirect
      // — a close caused by clicking elsewhere must let that target keep what
      // it took. `restoreComposerFocus` carries the rest, including the
      // modal bail, and is shared with the model picker beside us.
      if (rootRef.current?.contains(document.activeElement) !== true) return;
      restoreComposerFocus();
    });
  }, [hideHint]);

  // Close on click-outside / Escape, bound only while open.
  //
  // `pointerdown`, NOT `mousedown`: Radix's dropdown trigger calls
  // `preventDefault()` on pointerdown, which by spec suppresses the
  // compatibility mouse events, so a `mousedown` listener never hears the
  // click that opens the model picker and the panel hangs open beneath it.
  //
  // Escape is on WINDOW CAPTURE and claims the key, matching `AccountButton`,
  // `SessionRow` and `FolderRow`: Escape dismisses one layer, and this panel
  // is the top one while open. It is also the half `SettingsView`'s mount
  // sweep drives via a synthetic Escape on window, which is why the panel
  // carries `data-context-meter`. Without the sweep, this listener runs ahead
  // of the dialog's own document-capture handler and swallows the dialog's
  // first Escape — the panel itself is invisible (z-index 3, under the
  // backdrop) and holds nothing tabbable.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: Event): void => {
      if (rootRef.current?.contains(event.target as Node) === true) return;
      close();
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, close]);

  const view = useMemo(() => {
    if (usage == null) return null;
    const { usedTokens, limitTokens } = usage;
    // No window means nothing to render a fraction against. Written as
    // `!(x > 0)` so a NaN window is caught too, and placed here rather than at
    // the ratio because the legend would otherwise still divide and print
    // `NaN%`.
    if (!(limitTokens > 0)) return null;
    // The gateway knows the window before the fill — see
    // `SessionContextUsage.usedTokens`. Anything derived from the numerator
    // stays null rather than being faked to zero.
    const measured = usedTokens != null;
    const ratio = measured ? usedTokens / limitTokens : 0;
    // Free space is derived from the CLAMPED used figure so the two legend
    // rows always sum to the window, even if the gateway reports a prompt
    // larger than the limit it also reported (it can, briefly, around a
    // model switch).
    const used = measured ? Math.min(usedTokens, limitTokens) : null;
    const level: 'ok' | 'warn' | 'danger' =
      ratio >= DANGER_RATIO ? 'danger' : ratio >= WARN_RATIO ? 'warn' : 'ok';
    const percent = measured ? toPercent(ratio) : null;
    return {
      percent,
      // See `MIN_ARC_PERCENT`: a measured-but-tiny fill still gets a visible
      // tick, so "almost empty" never renders as "not measured".
      arcPercent: percent == null ? null : Math.max(percent, MIN_ARC_PERCENT),
      // Floored against its OWN share, not `100 - used`: the complement
      // rounds the wrong way and overstates headroom (82.3% true, 83%
      // advertised). The two rows may read 17/82 rather than summing to 100.
      freePercent:
        used == null ? null : toPercent((limitTokens - used) / limitTokens),
      level,
      usedTokens: used,
      freeTokens: used == null ? null : limitTokens - used,
      limitTokens,
      // A stale count is shown but marked, as the gateway's own UI marks it.
      prefix: usage.fresh ? '' : '~',
    };
  }, [usage]);

  /**
   * Dismiss when focus leaves the control entirely.
   *
   * The panel holds nothing tabbable, so a keyboard user pressing Tab moves to
   * whatever follows the trigger and the panel would otherwise stay parked
   * over the textarea, dismissible only by Escape or a click. `relatedTarget`
   * is where focus is going; a null one (focus falling to the body) is left
   * alone so a click on empty space doesn't fight the pointerdown handler.
   */
  const handleFocusOut = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      const next = event.relatedTarget;
      if (next == null) return;
      if (rootRef.current?.contains(next) === true) return;
      setOpen(false);
      hideHint();
    },
    [hideHint],
  );

  const toggle = useCallback(() => {
    if (open) close();
    else setOpen(true);
  }, [open, close]);

  // Without a reading there is nothing to draw, but the slot is still held —
  // see `Placeholder`. Any open panel or tooltip is dismissed rather than left
  // in state, so nothing pops back on the next reconnect and no document
  // listeners stay bound while nothing renders.
  const idle = view == null;
  useEffect(() => {
    if (idle) {
      setOpen(false);
      // Through `hideHint` so the pending DWELL goes too. Clearing the flag
      // alone left the timer armed: a cursor that entered the trigger just
      // before the reading dropped would set `hinted` behind the placeholder,
      // and since the trigger had unmounted no `mouseleave` was ever coming —
      // so the tooltip reappeared, stuck, when the reseed landed, with the
      // cursor long gone.
      hideHint();
    }
  }, [idle, hideHint]);

  if (view == null) {
    return <Placeholder aria-hidden="true" data-context-meter-placeholder />;
  }

  // Two labels, deliberately worded differently for two audiences.
  //
  // `summary` is spoken: screen readers read "/" and "%" poorly, so it spells
  // the reading out. `tooltip` is read: it matches Claude Code's meter
  // character for character. Because the trigger carries an `aria-label`, the
  // `title` is not announced, so the two never double up.
  const summary =
    view.usedTokens == null || view.percent == null
      ? `Context window ${formatTokens(view.limitTokens)}, usage not measured yet`
      : `Context window ${view.prefix}${formatTokens(view.usedTokens)} of ${formatTokens(view.limitTokens)} used, ${view.prefix}${view.percent} percent`;
  const tooltip = formatContextTooltip(view);

  return (
    // `data-manages-own-focus` opts the trigger out of the composer's
    // sticky-focus grab, which would otherwise pull focus back on click and
    // leave the panel open behind a caret that jumped away. A static attribute
    // rather than `aria-expanded`, because it has to be readable at blur time
    // and the composer's blur fires while `aria-expanded` still says collapsed.
    <Root ref={rootRef} onBlur={handleFocusOut}>
      {open && (
        /* A named `region`, not a `dialog`: nothing here is tabbable and
           focus is never moved in, and a dialog that never receives focus is
           typically not announced at all. The trigger carries the disclosure
           semantics. Stated explicitly so the landmark survives `Panel` being
           restyled onto a different element. */
        <Panel
          id={panelId}
          role="region"
          aria-label="Context window breakdown"
          // The hook `SettingsView`'s dismissal sweep looks for.
          data-context-meter
        >
          <PanelHeader>
            <PanelTitle>Context window</PanelTitle>
            <PanelValue>
              {view.usedTokens == null || view.percent == null ? (
                <>— / {formatTokens(view.limitTokens)}</>
              ) : (
                <>
                  {view.prefix}
                  {formatTokens(view.usedTokens)} /{' '}
                  {formatTokens(view.limitTokens)} ({view.prefix}
                  {view.percent}%)
                </>
              )}
            </PanelValue>
          </PanelHeader>

          {/* Decorative: the header states the same reading in text one line
              above, and exposing a progressbar too made a screen reader
              announce the numbers twice. */}
          <PanelBar aria-hidden="true">
            {view.percent != null && (
              <PanelBarFill
                $level={view.level}
                style={{ width: `${view.percent}%` }}
              />
            )}
          </PanelBar>

          {/* Split and per-run rows are omitted rather than dashed out while
              unmeasured: a panel of placeholders reads as broken, where one
              sentence reads as "not yet". */}
          {view.usedTokens == null ||
          view.freeTokens == null ||
          view.freePercent == null ||
          view.percent == null ? (
            <PendingNote>
              The gateway has no size for this chat yet. It fills in on your
              next message.
            </PendingNote>
          ) : (
            <>
              <Legend>
                <LegendRow>
                  <LegendLabel>
                    <Swatch $level={view.level} />
                    Context used
                  </LegendLabel>
                  <LegendValue>
                    <span>
                      {view.prefix}
                      {formatTokens(view.usedTokens)}
                    </span>
                    <LegendPercent>
                      {view.prefix}
                      {view.percent}%
                    </LegendPercent>
                  </LegendValue>
                </LegendRow>
                <LegendRow>
                  <LegendLabel>
                    <Swatch $level="free" />
                    Free space
                  </LegendLabel>
                  <LegendValue>
                    <span>{formatTokens(view.freeTokens)}</span>
                    <LegendPercent>{view.freePercent}%</LegendPercent>
                  </LegendValue>
                </LegendRow>
              </Legend>

              {view.prefix.length > 0 && (
                <StaleNote>
                  ~ Rebuilt from the stored transcript — the gateway has not
                  measured this chat on this connection yet.
                </StaleNote>
              )}
            </>
          )}
        </Panel>
      )}

      {/* Suppressed while the panel is open: the panel already carries these
          numbers, in more detail, in the same corner. */}
      {hinted && !open && (
        <Tooltip role="tooltip" aria-hidden="true">
          {tooltip}
        </Tooltip>
      )}

      <Trigger
        type="button"
        onClick={toggle}
        data-manages-own-focus
        onMouseEnter={() => showHint(HINT_DELAY_MS)}
        onMouseLeave={hideHint}
        onFocus={() => showHint(0)}
        onBlur={hideHint}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={summary}
        $open={open}
      >
        {/* The whole reading is on the button's `aria-label`, so the ring is
            decoration as far as assistive tech is concerned. An unmeasured
            chat draws the track alone — no arc — which reads as "nothing yet"
            rather than as a full-but-empty window. */}
        <Ring
          viewBox="0 0 16 16"
          width={RING_SIZE_PX}
          height={RING_SIZE_PX}
          aria-hidden="true"
        >
          <RingTrack cx="8" cy="8" r={RING_RADIUS} />
          {view.arcPercent != null && (
            <RingFill
              cx="8"
              cy="8"
              r={RING_RADIUS}
              $level={view.level}
              /* Mirrors the styled-component's colour choice as data, so the
                 pressure thresholds are assertable without resolving CSS
                 custom properties — which jsdom does not do. */
              data-level={view.level}
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={
                RING_CIRCUMFERENCE * (1 - view.arcPercent / 100)
              }
            />
          )}
        </Ring>
      </Trigger>
    </Root>
  );
}

// ── Styled Components ──────────────────────────────────────────────────

/**
 * The fill colour by pressure. `--accent-color` while there is room, then the
 * app's existing warning / error tokens — no new colours, so the meter
 * inherits every theme (light, dark, host-overridden) for free.
 */
const levelColor = (level: 'ok' | 'warn' | 'danger' | 'free'): string => {
  switch (level) {
    case 'danger':
      return 'var(--error-color)';
    case 'warn':
      return 'var(--warning-color)';
    case 'free':
      // `--text-label`, not `--border-color`. A hairline token is tuned to sit
      // at the edge of visibility, which is right for a 1px rule and wrong for
      // an 8px key the reader has to pick out: it measured 1.34:1 against the
      // panel in dark and 1.29:1 in light, both far under the 3:1 that
      // non-text UI is meant to clear. This is the app's muted-text token —
      // 8.1:1 dark, 5.3:1 light — so the key reads as clearly as its label.
      return 'var(--text-label)';
    default:
      return 'var(--accent-color)';
  }
};

/**
 * The hover tooltip. Page content rather than `title`: the native one waits
 * ~1s and is drawn by Chromium's browser-UI layer, so it never appears inside
 * the Electron webviews this app is previewed in.
 *
 * `aria-hidden` and deliberately not wired via `aria-describedby` — the
 * trigger's `aria-label` already carries the same reading in a form built for
 * speech. `pointer-events: none` so it cannot swallow the click it floats over.
 */
const Tooltip = styled.div`
  position: absolute;
  right: 0;
  bottom: calc(100% + 8px);
  z-index: 3;
  padding: 6px 10px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-color-container);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.24);
  color: var(--text-primary);
  font-size: 0.75rem;
  line-height: 1.125rem;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
  max-width: calc(100vw - 32px);
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * Holds the meter's slot while there is no reading, the same trick as
 * `ModelPickerPlaceholder` next door: unmounting slid the picker 22px right
 * and cost the row 4px of height, on every chat switch and reconnect. Sized
 * from the real trigger's constants.
 */
const Placeholder = styled.div`
  width: ${SLOT_SIZE_PX}px;
  height: ${SLOT_SIZE_PX}px;
  flex-shrink: 0;
`;

/**
 * Sits at the very end of the footer row, to the right of the model picker.
 *
 * The row is `justify-content: flex-end`, so a new LAST child pushes the
 * picker left rather than leaving it put — which is exactly why the slot has
 * to be reserved by `Placeholder` while there is no reading, instead of the
 * control unmounting.
 */
const Root = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

// Takes `ModelPicker`'s hover treatment so the two controls at this end of the
// footer read as one strip. The box is square rather than the picker's text
// pill — all it holds is the ring.
const Trigger = styled.button<{ readonly $open: boolean }>`
  all: unset;
  display: inline-flex;
  align-items: center;
  padding: ${TRIGGER_PADDING_PX}px;
  border-radius: 4px;
  color: var(--text-label);
  cursor: pointer;

  ${({ $open }) =>
    $open
      ? css`
          color: var(--text-primary);
          background-color: var(--hover-bg);
        `
      : ''}

  &:hover {
    color: var(--text-primary);
    background-color: var(--hover-bg);
  }

  &:focus-visible {
    outline: 2px solid var(--accent-color);
    outline-offset: -2px;
  }
`;

/**
 * The unfilled remainder, behind the fill.
 *
 * A share of `--text-label` rather than `--border-color`: at 70% of the border
 * token the track sat at 1.2:1 against its surface in both themes, which made
 * the ring effectively invisible at low percentages — you saw a floating arc,
 * not a gauge. Deliberately lighter than the legend key, since this one is a
 * backdrop for the fill rather than something to identify.
 */
const TRACK_COLOR = 'color-mix(in srgb, var(--text-label) 40%, transparent)';

const TRACK = css`
  border-radius: 999px;
  background: ${TRACK_COLOR};
  overflow: hidden;
`;

// `-90deg` so the arc starts at twelve o'clock and fills clockwise; SVG's own
// zero angle is at three o'clock, which reads as starting a quarter turn late.
const Ring = styled.svg`
  display: block;
  flex-shrink: 0;
  transform: rotate(-90deg);
  overflow: visible;
`;

const RingTrack = styled.circle`
  fill: none;
  stroke: ${TRACK_COLOR};
  stroke-width: 2;
`;

const RingFill = styled.circle<{
  readonly $level: 'ok' | 'warn' | 'danger';
}>`
  fill: none;
  stroke: ${({ $level }) => levelColor($level)};
  stroke-width: 2;
  stroke-linecap: round;
  transition: stroke-dashoffset 200ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * Opens UPWARD, like the composer's slash-command menu directly above it —
 * the footer is the last row on screen, so a downward panel would open off the
 * viewport. Anchored to the RIGHT edge because the meter is now the last
 * control in the row — a left-anchored 300px panel would hang off the side of
 * the composer. Positioned in flow (not portalled) for the same reason that
 * menu is: it dodges the ambient-zoom correction the portalled Radix menus
 * need.
 */
const Panel = styled.section`
  ${MENU_SURFACE}
  position: absolute;
  right: 0;
  bottom: calc(100% + 8px);
  z-index: 3;
  width: 300px;
  max-width: min(300px, calc(100vw - 32px));
  padding: 10px;
  gap: 8px;
  cursor: default;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const PanelTitle = styled.span`
  font-size: 0.75rem;
  color: var(--text-primary);
`;

const PanelValue = styled.strong`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const PanelBar = styled.div`
  ${TRACK}
  height: 6px;
`;

const PanelBarFill = styled.div<{ readonly $level: 'ok' | 'warn' | 'danger' }>`
  height: 100%;
  border-radius: inherit;
  background: ${({ $level }) => levelColor($level)};
  transition: width 200ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Legend = styled.dl`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  line-height: 1.25rem;
`;

/**
 * The legend key. Free space is an OUTLINE and used space a solid, so the two
 * differ in form rather than hue alone: at usable contrast against the panel
 * they sit at 1.00:1 against each other, i.e. identical in greyscale.
 */
const Swatch = styled.span<{
  readonly $level: 'ok' | 'warn' | 'danger' | 'free';
}>`
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 2px;
  ${({ $level }) =>
    $level === 'free'
      ? css`
          /* Longhands, not the \`border\` shorthand: the shorthand is what
             makes the hollow key unobservable to a computed-style check. */
          border-style: solid;
          border-width: 1px;
          border-color: ${levelColor('free')};
          background: transparent;
        `
      : css`
          background: ${levelColor($level)};
        `}
`;

const LegendLabel = styled.dt`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: var(--text-secondary);
`;

const LegendValue = styled.dd`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 0 auto;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
`;

// Fixed column so the two percentages line up under each other regardless of
// width ("9%" beside "91%" otherwise steps in and out.)
const LegendPercent = styled.span`
  width: 4ch;
  text-align: right;
  color: var(--text-label);
  font-variant-numeric: tabular-nums;
`;

/** Shown in place of the split while the gateway has no measurement yet. */
const PendingNote = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.125rem;
  color: var(--text-secondary);
`;

const StaleNote = styled.p`
  margin: 0;
  font-size: 0.6875rem;
  line-height: 1rem;
  color: var(--text-label);
`;
